import { MoreVertical } from "lucide-react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@pushpress/pushpress-ui";
import React from "react";
import { BadgeColor, StatusChip } from "@pushpress/shared-components";
import { useNavigate } from "../../../hooks/useNavigate";
import { cn } from "../../../utils/css";
import { SelectedMember } from "./checkinHelpers";

type CheckinMemberCardProps = {
  member?: SelectedMember;
  coachName?: string;
  coachImage?: string;
  isCoach?: boolean;
  isAssistant?: boolean;
  isMainCoach?: boolean;
  editLink?: string;
  handleCheckin?: () => void;
  handleUndoCheckin?: () => void;
  handleRemove?: () => void;
  coachStatus?: boolean;
};

export const CheckinMemberCard: React.FC<CheckinMemberCardProps> = ({
  member,
  isAssistant,
  coachName,
  coachImage,
  isMainCoach,
  editLink,
  handleCheckin,
  handleUndoCheckin,
  handleRemove,
  coachStatus,
}) => {
  const { firstName, lastName, status, primaryImage, membershipRole } =
    member || {};
  const navigate = useNavigate();
  const isAssignedCoach = isMainCoach || isAssistant;
  const isCheckedIn = coachStatus ? coachStatus : status === "checkedin";
  const showCheckInButton = !isCheckedIn;
  const name = isAssignedCoach ? coachName : `${firstName} ${lastName}`;
  const image = isAssignedCoach ? coachImage : primaryImage;

  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((item) => item![0])
    .join("");

  return (
    <div>
      <div
        className={cn(
          "tw-flex tw-items-center tw-p-4 tw-cursor-pointer tw-shadow-none tw-rounded-lg",
          "hover:tw-bg-gray-50 hover:tw-border-gray-200 tw-border tw-border-transparent",
        )}
      >
        <Avatar>
          <AvatarImage src={image} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="tw-flex-grow tw-ml-2">
          <p className="tw-text-sm tw-font-medium">{name}</p>
          {isAssignedCoach && (
            <p className="tw-text-xs tw-text-blue-500">
              {isMainCoach ? "Coach" : "Assistant Coach"}
            </p>
          )}
          {!isAssignedCoach && membershipRole && (
            <p className="tw-text-xs tw-text-gray-600">{membershipRole}</p>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {isCheckedIn && (
            <StatusChip
              label="Checked In"
              color={BadgeColor.Success}
              size="small"
            />
          )}

          {showCheckInButton && (
            <Button variant="outline" onClick={handleCheckin} size="sm">
              Check In
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="tw-h-4 tw-w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {isAssignedCoach && editLink && (
                <DropdownMenuItem
                  onClick={() => {
                    navigate(editLink);
                  }}
                >
                  Edit Assigned Coaches
                </DropdownMenuItem>
              )}
              {!isAssignedCoach && (
                <>
                  {isCheckedIn && (
                    <DropdownMenuItem onClick={handleUndoCheckin}>
                      Undo Check In
                    </DropdownMenuItem>
                  )}
                  {
                    <DropdownMenuItem onClick={handleRemove}>
                      Remove
                    </DropdownMenuItem>
                  }
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
